import { type StockTransformation } from "@artisancode/api-types";
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface Props {
  transformation: StockTransformation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (id: string, reason: string) => Promise<void>;
  isLoading?: boolean;
}

export function ProductionCancelDialog({
  transformation,
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: Props) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (open) setReason("");
  }, [open]);

  if (!transformation) return null;

  const isCompleted = transformation.status === "completed";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    await onConfirm(transformation.id, reason.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              <DialogTitle>Batalkan Batch Produksi</DialogTitle>
            </div>
            <DialogDescription className="pt-2">
              Anda akan membatalkan batch produksi{" "}
              <strong className="text-foreground">{transformation.code}</strong>
              .
            </DialogDescription>
          </DialogHeader>

          {isCompleted && (
            <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
              <strong>Perhatian:</strong> Batch ini berstatus <em>Selesai</em>.
              Membatalkannya akan membalikkan mutasi stok: stok barang jadi akan
              ditarik kembali dan bahan baku akan dikembalikan ke saldo stok
              toko.
            </div>
          )}

          <div className="mt-4 space-y-2">
            <Label htmlFor="cancel-reason">
              Alasan Pembatalan <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="cancel-reason"
              placeholder="Contoh: Kesalahan takaran, batch tumpah, atau salah input barang..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
              minLength={3}
            />
          </div>

          <DialogFooter className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
              className="min-h-[40px] sm:min-h-9"
            >
              Batal
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={!reason.trim() || reason.trim().length < 3 || isLoading}
              className="min-h-[40px] sm:min-h-9"
            >
              {isLoading ? "Memproses..." : "Ya, Batalkan Batch"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
